"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  SessionEntity,
  SessionStatus,
  RecordingState,
} from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import type {
  UseSessionLibraryParams,
  UseSessionLibraryReturn,
} from "~/modules/main/hooks/use-session-library/use-session-library.interface";

// Global state atoms
const selectedSessionIdAtom = atom<string | null>(null);
const searchQueryAtom = atom<string>("");
const recordingStateAtom = atom<RecordingState>({
  isRecording: false,
  isPaused: false,
  elapsedSeconds: 0,
  isMicrophonePermissionDenied: false,
});

export function useSessionLibrary(
  params: UseSessionLibraryParams,
): UseSessionLibraryReturn {
  const initialPageSize = params.initialPageSize ?? 20;

  // Global state
  const [selectedSessionId, setSelectedSessionId] = useAtom(
    selectedSessionIdAtom,
  );
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
  const [recording, setRecording] = useAtom(recordingStateAtom);

  // Local state for pagination
  const [offset, setOffset] = useState(0);

  // Recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Fetch sessions with pagination and search
  const {
    data: sessionsData,
    isLoading: isLoadingSessions,
    isError: isSessionsError,
  } = trpcReactQueryApi.main.sessions.all.useQuery({
    limit: initialPageSize,
    offset,
    searchQuery: searchQuery || undefined,
  });

  // Fetch total count
  const { data: totalCount } = trpcReactQueryApi.main.sessions.count.useQuery(
    undefined,
  );

  // Fetch selected session
  const {
    data: selectedSessionData,
    isLoading: isLoadingSelectedSession,
  } = trpcReactQueryApi.main.sessions.byId.useQuery(
    { id: selectedSessionId ?? "" },
    { enabled: !!selectedSessionId },
  );

  // Mutations
  const createSessionMutation =
    trpcReactQueryApi.main.sessions.create.useMutation({
      onSuccess: async () => {
        await utils.main.sessions.all.invalidate();
        await utils.main.sessions.count.invalidate();
      },
      onError: () => toast.error("Failed to create session"),
    });

  const renameSessionMutation =
    trpcReactQueryApi.main.sessions.rename.useMutation({
      onSuccess: async () => {
        toast.success("Session renamed successfully");
        await utils.main.sessions.all.invalidate();
        await utils.main.sessions.byId.invalidate();
      },
      onError: () => toast.error("Failed to rename session"),
    });

  const deleteSessionMutation =
    trpcReactQueryApi.main.sessions.delete.useMutation({
      onSuccess: async () => {
        toast.success("Session deleted successfully");
        await utils.main.sessions.all.invalidate();
        await utils.main.sessions.count.invalidate();
        setSelectedSessionId(null);
      },
      onError: () => toast.error("Failed to delete session"),
    });

  const processRecordingMutation =
    trpcReactQueryApi.main.sessions.processRecording.useMutation({
      onSuccess: async () => {
        toast.success("Recording uploaded and processing started");
        await utils.main.sessions.all.invalidate();
        await utils.main.sessions.byId.invalidate();
      },
      onError: () => toast.error("Failed to process recording"),
    });

  // Start recording timer
  const startTimer = useCallback(() => {
    if (timerIntervalRef.current) return;
    timerIntervalRef.current = setInterval(() => {
      setRecording((prev) => ({
        ...prev,
        elapsedSeconds: prev.elapsedSeconds + 1,
      }));
    }, 1000);
  }, [setRecording]);

  // Stop recording timer
  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, [stopTimer]);

  // Commands
  const formatDuration = useCallback((seconds: number | null): string => {
    if (seconds === null) return "00:00";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const getStatusForSession = useCallback((session: SessionEntity) => {
    return session.status;
  }, []);

  // Queries
  const getFilteredSessions = useCallback(() => {
    return sessionsData ?? [];
  }, [sessionsData]);

  // Mutations
  const selectSession = useCallback(
    () => ({
      mutate: (sessionId: string | null) => {
        setSelectedSessionId(sessionId);
      },
      loading: isLoadingSelectedSession,
      error: null,
    }),
    [setSelectedSessionId, isLoadingSelectedSession],
  );

  const updateSearchQuery = useCallback(
    () => ({
      mutate: (query: string) => {
        setSearchQuery(query);
        setOffset(0); // Reset pagination on search
      },
      loading: false,
      error: null,
    }),
    [setSearchQuery],
  );

  const loadMoreSessions = useCallback(
    () => ({
      mutate: () => {
        setOffset((prev) => prev + initialPageSize);
      },
      loading: isLoadingSessions,
      error: null,
    }),
    [initialPageSize, isLoadingSessions],
  );

  const renameSession = useCallback(
    () => ({
      mutate: (id: string, newTitle: string) => {
        renameSessionMutation.mutate({ id, title: newTitle });
      },
      loading: renameSessionMutation.isPending,
      error: renameSessionMutation.error?.message ?? null,
    }),
    [renameSessionMutation],
  );

  const deleteSession = useCallback(
    () => ({
      mutate: (id: string) => {
        deleteSessionMutation.mutate({ id });
      },
      loading: deleteSessionMutation.isPending,
      error: deleteSessionMutation.error?.message ?? null,
    }),
    [deleteSessionMutation],
  );

  const startRecording = useCallback(
    () => ({
      mutate: async () => {
        try {
          // Request microphone permission
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });

          // Create session
          const session = await createSessionMutation.mutateAsync({
            title: `Recording ${format(new Date(), "MMM d, yyyy HH:mm")}`,
          });
          currentSessionIdRef.current = session.id;

          // Initialize MediaRecorder
          const mediaRecorder = new MediaRecorder(stream);
          mediaRecorderRef.current = mediaRecorder;
          audioChunksRef.current = [];

          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              audioChunksRef.current.push(event.data);
            }
          };

          mediaRecorder.start();
          startTimer();

          setRecording({
            isRecording: true,
            isPaused: false,
            elapsedSeconds: 0,
            isMicrophonePermissionDenied: false,
          });

          toast.success("Recording started");
        } catch (error) {
          if (
            error instanceof Error &&
            error.name === "NotAllowedError"
          ) {
            setRecording((prev) => ({
              ...prev,
              isMicrophonePermissionDenied: true,
            }));
            toast.error("Microphone permission denied");
          } else {
            toast.error("Failed to start recording");
          }
        }
      },
      loading: createSessionMutation.isPending,
      error: createSessionMutation.error?.message ?? null,
    }),
    [createSessionMutation, startTimer, setRecording],
  );

  const pauseRecording = useCallback(
    () => ({
      mutate: () => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === "recording"
        ) {
          mediaRecorderRef.current.pause();
          stopTimer();
          setRecording((prev) => ({ ...prev, isPaused: true }));
          toast.success("Recording paused");
        }
      },
      loading: false,
      error: null,
    }),
    [stopTimer, setRecording],
  );

  const resumeRecording = useCallback(
    () => ({
      mutate: () => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === "paused"
        ) {
          mediaRecorderRef.current.resume();
          startTimer();
          setRecording((prev) => ({ ...prev, isPaused: false }));
          toast.success("Recording resumed");
        }
      },
      loading: false,
      error: null,
    }),
    [startTimer, setRecording],
  );

  const stopRecording = useCallback(
    () => ({
      mutate: async () => {
        if (!mediaRecorderRef.current || !currentSessionIdRef.current) return;

        const sessionId = currentSessionIdRef.current;
        const durationSeconds = recording.elapsedSeconds;

        mediaRecorderRef.current.onstop = async () => {
          try {
            // Create audio blob
            const audioBlob = new Blob(audioChunksRef.current, {
              type: "audio/webm",
            });

            // Convert to base64
            const reader = new FileReader();
            reader.readAsDataURL(audioBlob);
            reader.onloadend = async () => {
              const base64Audio = reader.result as string;
              const base64Data = base64Audio.split(",")[1];
              if (!base64Data) {
                toast.error("Failed to process audio");
                return;
              }

              // Upload and process
              await processRecordingMutation.mutateAsync({
                sessionId,
                audioBuffer: base64Data,
                durationSeconds,
                mimeType: "audio/webm",
              });
            };

            // Stop all tracks
            mediaRecorderRef.current
              ?.stream.getTracks()
              .forEach((track) => track.stop());

            // Reset state
            stopTimer();
            setRecording({
              isRecording: false,
              isPaused: false,
              elapsedSeconds: 0,
              isMicrophonePermissionDenied: false,
            });
            audioChunksRef.current = [];
            currentSessionIdRef.current = null;

            toast.success("Recording stopped and uploaded");
          } catch (error) {
            toast.error("Failed to upload recording");
          }
        };

        mediaRecorderRef.current.stop();
      },
      loading: processRecordingMutation.isPending,
      error: processRecordingMutation.error?.message ?? null,
    }),
    [recording, processRecordingMutation, stopTimer, setRecording],
  );

  const downloadAudio = useCallback(
    () => ({
      mutate: (id: string) => {
        try {
          const session = sessionsData?.find((s) => s.id === id);
          if (!session?.audioUrl) {
            toast.error("Audio not available");
            return;
          }
          // Detect file extension from audioUrl
          let extension = "webm"; // Default fallback
          try {
            const url = new URL(session.audioUrl);
            const pathname = url.pathname;
            const lastDot = pathname.lastIndexOf(".");
            if (lastDot !== -1) {
              const detectedExt = pathname.substring(lastDot + 1).toLowerCase();
              if (detectedExt) {
                extension = detectedExt;
              }
            }
          } catch (e) {
            // If URL parsing fails, use default extension
          }
          // Create download link with native format
          const link = document.createElement("a");
          link.href = session.audioUrl;
          link.download = `${session.title}.${extension}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success("Download started");
        } catch (error) {
          toast.error("Failed to download audio");
        }
      },
      loading: false,
      error: null,
    }),
    [sessionsData],
  );

  const downloadTranscript = useCallback(
    () => ({
      mutate: async (id: string) => {
        try {
          // Fetch transcript content from server
          const result = await utils.main.transcripts.downloadAsText.fetch({
            sessionId: id,
          });
          if (!result) {
            toast.error("Transcript not available");
            return;
          }
          // Create text file blob
          const blob = new Blob([result.content], { type: "text/plain" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = result.filename || `transcript-${id}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast.success("Transcript downloaded");
        } catch (error) {
          toast.error("Failed to download transcript");
        }
      },
      loading: false,
      error: null,
    }),
    [utils],
  );

  const downloadSummary = useCallback(
    () => ({
      mutate: (id: string) => {
        try {
          const session = sessionsData?.find((s) => s.id === id);
          if (!session?.summaryBullets) {
            toast.error("Summary not available");
            return;
          }
          // Create text file
          const content = session.summaryBullets.join("\n");
          const blob = new Blob([content], { type: "text/plain" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${session.title}-summary.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast.success("Summary downloaded");
        } catch (error) {
          toast.error("Failed to download summary");
        }
      },
      loading: false,
      error: null,
    }),
    [sessionsData],
  );

  return {
    // State
    sessions: sessionsData ?? [],
    totalSessions: totalCount ?? 0,
    selectedSessionId,
    selectedSession: selectedSessionData ?? null,
    isLoading: isLoadingSessions,
    isLoadingMore: false,
    searchQuery,
    recording,
    selectedProcessingPercent:
      selectedSessionData?.processing.progressPercent ?? null,

    // Queries
    getFilteredSessions,

    // Mutations
    selectSession,
    updateSearchQuery,
    loadMoreSessions,
    renameSession,
    deleteSession,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    downloadAudio,
    downloadTranscript,
    downloadSummary,

    // Commands
    formatDuration,
    getStatusForSession,
  };
}